import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../services/user/auth-service';
import { EventService } from '../services/internal/event-service';


/** User must be logged out, 
 *  Failing will redirect them to the home page */
@Injectable({
  providedIn: 'root'
})
export class LoggedOutGuard  {
    constructor(public auth: AuthService, private router: Router) { }

    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.auth.isLoggedOut().then((result) => {
                if (!result)
                    this.router.navigateByUrl("/");

                resolve(result);
            });
        });
    }
}


/** User must be logged in 
 *  Failing will emit a 401 error in the error service for you to catch */
@Injectable({
  providedIn: 'root'
})
export class LoggedInGuard  {
    constructor(public auth: AuthService, public error: EventService) { }

    /** Checks if a user is logged in */
    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.auth.isLoggedIn().then((result) => {
                if (!result)
                {
                    this.auth.returnUrlAfterLogin = state.url;
                    this.error.fail401.emit();
                }

                resolve(result);
            });
        });
    }
}


/** User must be an admin
 *  Failing will emit a 403 error in the error service for you to catch */
@Injectable({
  providedIn: 'root'
})
export class IsAdminGuard  {
    constructor(public auth: AuthService, public error: EventService) { }

    /** Checks if a user is logged in */
    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.auth.isAdmin().then((result) => {
                if (!result)
                    this.error.fail403.emit();

                if (!this.auth.tokenUser)
                    this.error.fail401.emit();

                resolve(result);
            });
        });
    }
}


/** User must be the owner of the route, or an admin */
@Injectable({
  providedIn: 'root'
})
export class IsAdminOrOwnerGuard  {
    constructor(public auth: AuthService, public error: EventService) { }

    /** Checks if a user is logged in */
    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.auth.isAdmin()
                .then((userIsAdmin) => {
                    if (userIsAdmin)
                    {
                        resolve(true);
                    }
                    else
                    {
                        if (!this.auth.tokenUser)              // No logged in user
                        {
                            resolve(false);
                            this.error.fail401.emit();
                        }
                        else if (this.isProfileOwner(next))    // Owns the profile page
                        {
                            resolve(true);
                        }
                     /*   else if (this.isSightingOwner(next))    // Owns the Sighting page
                        {
                            resolve(true);
                        }
                        else if (this.isAbsenceOwner(next))    // Owns the Absence page
                        {
                            resolve(true);
                        }*/
                        else                                   // Not an admin or owner
                        {
                            resolve(false);
                            this.error.fail403.emit();
                        }
                    }
                })
                .catch(() => {
                    console.error("Error checking admin status");
                });
        });
    }

    /** Returns true if the route is a profile edit and the user is the owner */
    isProfileOwner(route: ActivatedRouteSnapshot): boolean {
        if (route.url[0].path == "user" && route.url[2].path == "edit")
            return ((route.url[1].path == this.auth.tokenUser?.username));

        return false;
    }
}


export interface ComponentCanDeactivate {
  canDeactivate: () => boolean | Observable<boolean>;
}

@Injectable({ providedIn: 'root' })
export class PendingChangesGuard  {
  canDeactivate(component: ComponentCanDeactivate): boolean | Observable<boolean> {
    // if there are no pending changes, just allow deactivation; else confirm first
    return component.canDeactivate() ?
      true :
      // NOTE: this warning message will only be shown when navigating elsewhere within your angular app;
      // when navigating away from your angular app, the browser will show a generic warning message
      // see http://stackoverflow.com/a/42207299/7307355
      confirm('WARNING: You have unsaved changes. Press Cancel to go back and save these changes, or OK to lose these changes.');
  }
}